import { OrgPaymentTier } from '@kindo/universal';
import React, { useState } from 'react';

import { ApiTab } from './ApiSettings';
import { PluginsTab } from './Plugins';
import { OrgSecuritySettingsTabs } from './SecuritySettings';
import {
  SettingsStructureItem,
  SettingsStructureSubItem,
  SettingsTab
} from './SettingsModal.types';
import {
  ALL_TAB_COMPONENTS,
  DEFAULT_TAB_COMPONENT,
  getSettingsTabs
} from './SettingsModal.utils';

import {
  Icon,
  Icons,
  Modal,
  ModalWidth,
  TextIconColor,
  Typography,
  TypographyCasing,
  TypographyFont,
  TypographySize,
  TypographyWeight
} from '~/components/core';
import { useAppSelector, useCanAccessSecuritySettings } from '~/hooks';

type SettingsModalProps = {
  initialTab: SettingsTab;
  onClose: () => void;
};

export const SettingsModal = ({ onClose, initialTab }: SettingsModalProps) => {
  const [activeTab, setActiveTab] = useState<
    SettingsTab | ApiTab | OrgSecuritySettingsTabs | PluginsTab
  >(initialTab);

  const [focusedTab, setFocusedTab] = useState<SettingsTab>(initialTab);

  const [isFocused, setIsFocused] = useState(false);

  const { org } = useAppSelector((state) => state.user);
  const isEnterprise = org?.paymentTier === OrgPaymentTier.ENTERPRISE;
  const { userCanAccessSecuritySettings } = useCanAccessSecuritySettings();

  const settingsStructure = getSettingsTabs(
    userCanAccessSecuritySettings,
    isEnterprise
  );

  // Simply look up the component directly from our unified mapping
  const activeComponent =
    ALL_TAB_COMPONENTS[activeTab] || DEFAULT_TAB_COMPONENT;

  const handleSettingsTabClick = (tab: SettingsStructureItem) => {
    setIsFocused((prevFocused) => !(prevFocused && tab.tab === focusedTab));
    setFocusedTab(tab.tab);

    if (tab.hasComponent) {
      setActiveTab(tab.tab);
    }
  };

  const handleSettingsSubTabClick = (subTab: SettingsStructureSubItem) => {
    setActiveTab(subTab.tab);
  };

  return (
    <Modal
      noContentPadding
      onClose={onClose}
      open
      overflowAuto={false}
      width={ModalWidth.TWELVE_HUNDRED}
    >
      <div className="flex w-full flex-row" data-testid="settings-wrapper">
        <div
          className="bg-primary-model-menu flex w-48 flex-col gap-1 px-[10px] py-5"
          data-testid="settings-sidebar-container"
        >
          {Object.values(settingsStructure).map((tab) => (
            <React.Fragment key={tab.tab}>
              <div
                className="border-primary-border-alt flex cursor-pointer flex-row items-center border-b pb-[6px] pt-1"
                data-testid={`settings-sidebar-item-${tab.tab}`}
                onClick={() => {
                  handleSettingsTabClick(tab);
                }}
              >
                <Typography
                  casing={TypographyCasing.UPPERCASE}
                  color={
                    focusedTab === tab.tab
                      ? TextIconColor.PRIMARY
                      : TextIconColor.SECONDARY
                  }
                  font={TypographyFont.INTERACTIVE}
                  key={tab.tab}
                  size={TypographySize.SMALL}
                  weight={TypographyWeight.SEMI_BOLD}
                >
                  {tab.displayName}
                </Typography>
                {tab.subTabs.length > 0 && (
                  <Icons
                    color={
                      focusedTab === tab.tab
                        ? TextIconColor.PRIMARY
                        : TextIconColor.SECONDARY
                    }
                    icon={
                      focusedTab === tab.tab && isFocused
                        ? Icon.CHEVRON_DOWN
                        : Icon.CHEVRON_RIGHT
                    }
                  />
                )}
              </div>
              {tab.subTabs.length > 0 &&
                focusedTab === tab.tab &&
                isFocused && (
                  <div
                    className="flex flex-col gap-[2px] px-[15px] pt-[6px]"
                    data-testid={`settings-sidebar-subtab-container-${tab.tab}`}
                  >
                    {tab.subTabs.map((subTab) => (
                      <div
                        className="flex cursor-pointer flex-row items-center px-[2px] py-1"
                        data-testid={`settings-sidebar-subtab-${subTab.tab}`}
                        key={subTab.tab}
                        onClick={() => {
                          handleSettingsSubTabClick(subTab);
                        }}
                      >
                        <Typography
                          casing={TypographyCasing.UPPERCASE}
                          color={
                            activeTab === subTab.tab
                              ? TextIconColor.PRIMARY
                              : TextIconColor.SECONDARY
                          }
                          font={TypographyFont.INTERACTIVE}
                          key={subTab.tab}
                          size={TypographySize.X_SMALL}
                          weight={TypographyWeight.SEMI_BOLD}
                        >
                          {subTab.displayName}
                        </Typography>
                      </div>
                    ))}
                  </div>
                )}
            </React.Fragment>
          ))}
        </div>
        <div
          className="relative flex h-[80vh] flex-1 flex-col overflow-y-auto px-6 py-4"
          data-testid="settings-page-container"
          style={{ isolation: 'isolate' }}
        >
          {activeComponent}
        </div>
      </div>
    </Modal>
  );
};

export default SettingsModal;
